// src/auth/authRefreshBridge.ts
//
// Module-level bridge that lets non-React code (e.g. fetch-response handlers
// in `src/api/utils/errorHandler.ts`) trigger an auth-token refresh without
// importing React or AuthContext.
//
// AuthContext registers a `triggerRefresh` callback on mount. When a backend
// call returns 401/403, `errorHandler.ts` calls `triggerAuthRefreshIfRegistered()`
// which fires the registered callback (no-op if nothing is registered — e.g.
// during tests, or if AuthProvider hasn't mounted yet).
//
// The refresh is **fire-and-forget for the current call** — the 401 response
// is still propagated to the caller. Subsequent API calls will see the
// refreshed token.
//
// Guarantees:
// - **In-flight dedup**: if a refresh is already running, a second 401 during
//   that refresh is ignored (prevents two concurrent refreshes). The
//   previous wall-clock debounce was not enough: a real refresh can take
//   10+ seconds (MSAL's iframeBridgeTimeout) which exceeds any reasonable
//   debounce window.
// - **Circuit breaker**: after CIRCUIT_MAX_REFRESHES consecutive refreshes
//   without a cooldown of CIRCUIT_COOLDOWN_MS, further triggers are
//   suppressed until the cooldown elapses. Prevents the pathological case
//   where every API call returns 401 even after a successful refresh
//   (user deactivated in DB, bad scope) from flooding PostHog and spinning
//   the user's token endpoint.

type TriggerRefreshFn = () => Promise<void>;

let registeredTrigger: TriggerRefreshFn | null = null;
let inFlightRefresh: Promise<void> | null = null;

// Circuit breaker state
let refreshCountInWindow = 0;
let windowStartMs = 0;
const CIRCUIT_MAX_REFRESHES = 3;
const CIRCUIT_WINDOW_MS = 30_000;
const CIRCUIT_COOLDOWN_MS = 60_000;
let circuitOpenUntilMs = 0;

// Cross-tab refresh coordination via BroadcastChannel. All Word Online tabs
// served from the same origin share this channel, so a refresh in tab A can
// suppress duplicate refreshes in tab B during the same window. MSAL's
// localStorage cache is the source of truth for the token itself; this
// channel is just rate-limiting.
const MULTI_TAB_CHANNEL_NAME = "cs-auth-refresh";
const MULTI_TAB_SUPPRESS_MS = 10_000;
let multiTabSuppressUntilMs = 0;
let broadcastChannel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    broadcastChannel = new BroadcastChannel(MULTI_TAB_CHANNEL_NAME);
    broadcastChannel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === "refresh-started") {
        multiTabSuppressUntilMs = Date.now() + MULTI_TAB_SUPPRESS_MS;
      }
    };
  }
} catch {
  /* BroadcastChannel unsupported (very old browsers) — skip cross-tab coord */
}

/**
 * Register the refresh callback. Called by AuthContext on mount.
 * Returns a teardown function that unregisters it.
 */
export function registerAuthRefresh(trigger: TriggerRefreshFn): () => void {
  if (registeredTrigger !== null && registeredTrigger !== trigger) {
    // Last-writer-wins is intended (e.g. StrictMode double-mount, test setup).
    // A warning helps diagnose if two AuthProviders ever truly co-exist.
    // eslint-disable-next-line no-console
    console.warn("[authRefreshBridge] registerAuthRefresh overwrote an existing trigger");
  }
  registeredTrigger = trigger;
  return () => {
    if (registeredTrigger === trigger) registeredTrigger = null;
  };
}

/**
 * Trigger a refresh if (a) a callback is registered, (b) no refresh is
 * currently in flight, and (c) the circuit breaker is not open. Returns the
 * in-flight promise so callers can optionally await it (errorHandler.ts
 * ignores the return value — fire-and-forget).
 */
export function triggerAuthRefreshIfRegistered(): Promise<void> | null {
  if (!registeredTrigger) return null;

  // Circuit breaker: refuse if we've tripped it.
  const now = Date.now();
  if (now < circuitOpenUntilMs) return null;

  // Cross-tab coordination: another tab announced a refresh recently.
  if (now < multiTabSuppressUntilMs) return null;

  // If a refresh is already in flight, don't queue a second.
  if (inFlightRefresh) return inFlightRefresh;

  // Update the rolling-window count.
  if (now - windowStartMs > CIRCUIT_WINDOW_MS) {
    // New window.
    windowStartMs = now;
    refreshCountInWindow = 1;
  } else {
    refreshCountInWindow += 1;
  }

  if (refreshCountInWindow > CIRCUIT_MAX_REFRESHES) {
    // eslint-disable-next-line no-console
    console.warn(
      `[authRefreshBridge] circuit breaker tripped: ${refreshCountInWindow} refreshes in ${CIRCUIT_WINDOW_MS}ms — suppressing for ${CIRCUIT_COOLDOWN_MS}ms`
    );
    circuitOpenUntilMs = now + CIRCUIT_COOLDOWN_MS;
    // Reset the window so we don't immediately re-trip after cooldown.
    refreshCountInWindow = 0;
    windowStartMs = 0;
    return null;
  }

  // Announce to other tabs that we're starting a refresh so they can
  // suppress duplicate refreshes for MULTI_TAB_SUPPRESS_MS.
  try {
    broadcastChannel?.postMessage({ type: "refresh-started", at: now });
  } catch {
    /* non-fatal */
  }

  const promise = registeredTrigger()
    .catch(() => {
      /* intentionally swallowed — caller contract is fire-and-forget */
    })
    .finally(() => {
      if (inFlightRefresh === promise) inFlightRefresh = null;
    });
  inFlightRefresh = promise;
  return promise;
}

/** Reset internal state. Test-only. */
export function _resetAuthRefreshBridgeForTesting(): void {
  registeredTrigger = null;
  inFlightRefresh = null;
  refreshCountInWindow = 0;
  windowStartMs = 0;
  circuitOpenUntilMs = 0;
  multiTabSuppressUntilMs = 0;
}
